import {
    CONSENT_REJECTED_TTL_DAYS,
    CONSENT_STORAGE_KEY,
    CONSENT_STORAGE_VERSION,
    CONSENT_TTL_DAYS,
} from './consent.constants';
import type { ServiceConsentState } from './consent.types';
import { isAcceptingConsent } from './consent-services';
import { clearConsentCookie, writeConsentCookie } from './consent-cookie';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Rejections expire far earlier than acceptances. The stored state has to follow
 * the cookie here — otherwise the server would drop the cookie and re-render the
 * banner while localStorage still reported a decision, and the banner would
 * vanish again on hydration.
 */
const ttlMs = (accepted: boolean): number => (accepted ? CONSENT_TTL_DAYS : CONSENT_REJECTED_TTL_DAYS) * DAY_MS;

type StoredConsent = {
    version: number;
    decidedAt: number;
    /** Whether the user made an explicit, full decision (accept/reject/save). */
    decided: boolean;
    /**
     * One explicit entry per consent-relevant service. The key set doubles as
     * the record of which services the visitor was informed about — a service
     * missing here has never been consented to and re-opens the banner.
     */
    services: ServiceConsentState;
};

export type StoredConsentResult = {
    serviceConsent: ServiceConsentState;
    decided: boolean;
};

/** Keep only booleans — the map is keyed by arbitrary, possibly stale service ids. */
export function normalizeServiceConsent(input: unknown): ServiceConsentState {
    if (!input || typeof input !== 'object') return {};

    const state: ServiceConsentState = {};
    for (const [id, value] of Object.entries(input)) {
        if (typeof value === 'boolean') state[id] = value;
    }
    return state;
}

export function readStoredConsent(): StoredConsentResult | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw) as StoredConsent;
        if (!parsed || parsed.version !== CONSENT_STORAGE_VERSION) return null;

        const serviceConsent = normalizeServiceConsent(parsed.services);
        const expiresAt = parsed.decidedAt + ttlMs(isAcceptingConsent(serviceConsent));
        if (!Number.isFinite(parsed.decidedAt) || expiresAt <= Date.now()) {
            window.localStorage.removeItem(CONSENT_STORAGE_KEY);
            clearConsentCookie();
            return null;
        }

        return { serviceConsent, decided: Boolean(parsed.decided) };
    } catch {
        return null;
    }
}

export function persistConsent(serviceConsent: ServiceConsentState, decided: boolean): void {
    if (typeof window === 'undefined') return;
    try {
        const payload: StoredConsent = {
            version: CONSENT_STORAGE_VERSION,
            decidedAt: Date.now(),
            decided,
            services: serviceConsent,
        };
        window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
    } catch {
        // storage may be unavailable (private mode / quota) — decision stays in-memory for this session
    }

    // Keep the server-readable mirror in sync (see CONSENT_COOKIE_ACCEPTED_KEY).
    if (decided) writeConsentCookie(isAcceptingConsent(serviceConsent));
    else clearConsentCookie();
}

export function clearStoredConsent(): void {
    if (typeof window === 'undefined') return;
    clearConsentCookie();
    try {
        window.localStorage.removeItem(CONSENT_STORAGE_KEY);
    } catch {
        // ignore
    }
}
